// src/pages/EditHelperProfile.js
import { useState, useEffect } from "react";
import { auth } from "../firebase";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { deleteUser } from "firebase/auth";
import { deleteAccountAndData } from "../utils/deleteAccount";
import { BRAND } from "../config/brand";
import BrandMark from "../components/BrandMark";

// ✅ centralized helper profile service
import { getUserProfile, upsertHelperProfile } from "../services/userService";
import { TAG_CATALOG, normalizePredefinedTag } from "../utils/predefinedTags";
import { TIMEZONES, findTimezone, getDefaultTimezone } from "../utils/timezones";
import { ChevronDownIcon } from "@heroicons/react/24/solid";

function EditHelperProfile() {
  const navigate = useNavigate();
  const { currentUserRole } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    selectedTags: [],
    bio: "",
    availability: "online",
    preferredUrgency: "medium",
    hourlyRate: "",
    timezone: getDefaultTimezone(),
  });
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ✅ Load existing profile (if helper already set up)
  useEffect(() => {
    async function fetchProfile() {
      try {
        if (!auth.currentUser) return;
        const profile = await getUserProfile(auth.currentUser.uid);

        if (profile && profile.success && profile.data) {
          const data = profile.data;
          // Show every tag the helper already has, across all fields of
          // expertise — no longer locked to a single category.
          setFormData({
            name: data.name || "",
            email: data.email || "",
            selectedTags: (data.skills || []).map(normalizePredefinedTag),
            bio: data.bio || "",
            availability: data.availability || "online",
            preferredUrgency: data.preferredUrgency || "medium",
            hourlyRate: data.hourlyRate || "",
            timezone: findTimezone(data.location?.timezone) || getDefaultTimezone(),
          });
        } else {
          // 🔔 First time setup: force user to complete profile
          toast.info("Please complete your helper profile to start helping!");
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast.error("Failed to load profile.");
      }
    }
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // No cap — helpers can select as many tags as apply, across any field.
  const handleToggleTag = (tag) => {
    setFormData((prev) => {
      const t = normalizePredefinedTag(tag);
      const has = prev.selectedTags.includes(t);
      const next = has
        ? prev.selectedTags.filter((x) => x !== t)
        : [...prev.selectedTags, t];
      return { ...prev, selectedTags: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      (formData.selectedTags || []).length === 0 ||
      !formData.bio ||
      !formData.hourlyRate
    ) {
      toast.error("Please fill out all required fields");
      return;
    }

    try {
      setLoading(true);

      // 1️⃣ Use predefined tags as skills (no cap — any number, any field)
      const skillsArray = Array.from(
        new Set((formData.selectedTags || []).map(normalizePredefinedTag))
      );

      // 2️⃣ Save updated profile (preserve location with timezone)
      const selectedTimezone = findTimezone(formData.timezone) || getDefaultTimezone();
      await upsertHelperProfile(
        {
          name: formData.name,
          email: formData.email,
          role: "helper",
          skills: skillsArray,
          bio: formData.bio,
          availability: formData.availability,
          preferredUrgency: formData.preferredUrgency,
          hourlyRate: Number(formData.hourlyRate),
          location: {
            timezone: selectedTimezone,
          },
        },
        auth.currentUser.uid
      );

      toast.success("Profile updated successfully!");
      navigate("/dashboard-helper");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!auth.currentUser) return;
    try {
      setDeleting(true);
      await deleteAccountAndData(auth.currentUser.uid, currentUserRole || "helper");
      await deleteUser(auth.currentUser);
      localStorage.removeItem("currentUser");
      localStorage.removeItem("userRole");
      toast.success("Account deleted successfully.");
      navigate("/login");
    } catch (error) {
      console.error("Account deletion failed", error);
      toast.error("Account deletion failed. Please contact support.");
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-4 sm:py-8 px-4 sm:px-6">
      <div className="mx-auto flex max-w-xl items-center gap-2 pb-4">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark size={32} />
          <span className="text-base font-semibold text-slate-900">{BRAND.name}</span>
        </Link>
      </div>
      <div className="max-w-xl mx-auto bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
            Complete your helper profile
          </h2>
          <button
            onClick={() => navigate(currentUserRole === 'helper' ? '/dashboard-helper' : '/dashboard-client')}
            className="rounded-lg border border-slate-300 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
          >
            Dashboard
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <input
          type="text"
          name="name"
          placeholder="Full name"
          className="w-full border border-slate-300 rounded-lg px-3 py-2"
          value={formData.name}
          onChange={handleChange}
          required
        />
        <input
          type="email"
          name="email"
          placeholder="Email"
          className="w-full border border-slate-300 rounded-lg px-3 py-2"
          value={formData.email}
          onChange={handleChange}
          required
        />
        <div>
          <label className="block text-sm font-medium mb-1 text-slate-700">
            Select Hashtags (your expertise)
          </label>
          <p className="text-xs text-slate-500 mb-3">
            Select your domain of expertise and the relevant tags.
          </p>
          <div className="space-y-4">
            {TAG_CATALOG.map((cat) => (
              <div key={cat.id}>
                <p className="font-medium text-slate-800 mb-1">{cat.label}</p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {cat.tags.map((tag) => {
                    const normalized = normalizePredefinedTag(tag);
                    const selected = formData.selectedTags.includes(normalized);
                    return (
                      <button
                        type="button"
                        key={tag}
                        onClick={() => handleToggleTag(tag)}
                        className={`px-2 sm:px-3 py-1 rounded-full border text-xs sm:text-sm ${
                          selected
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-700 border-slate-300"
                        }`}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {formData.selectedTags.length > 0 && (
            <p className="mt-2 text-sm text-slate-600">
              Selected ({formData.selectedTags.length}): {formData.selectedTags.map((t) => `#${t}`).join(", ")}
            </p>
          )}
        </div>
        <textarea
          name="bio"
          placeholder="Brief bio or experience"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 h-24"
          value={formData.bio}
          onChange={handleChange}
          required
        />

        {/* ✅ Availability dropdown */}
        <div>
          <label className="block text-sm font-medium mb-1 text-slate-700">
            Availability
          </label>
          <select
            name="availability"
            className="w-full border border-slate-300 rounded-lg px-3 py-2"
            value={formData.availability}
            onChange={handleChange}
            required
          >
            <option value="online">Online</option>
            <option value="busy">Busy</option>
            <option value="offline">Offline</option>
          </select>
        </div>

        {/* ✅ Preferred Urgency dropdown */}
        <div>
          <label className="block text-sm font-medium mb-1 text-slate-700">
            Preferred Urgency
          </label>
          <select
            name="preferredUrgency"
            className="w-full border border-slate-300 rounded-lg px-3 py-2"
            value={formData.preferredUrgency}
            onChange={handleChange}
            required
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <input
          type="number"
          name="hourlyRate"
          placeholder="Hourly Rate ($)"
          className="w-full border border-slate-300 rounded-lg px-3 py-2"
          value={formData.hourlyRate}
          onChange={handleChange}
          required
        />

        {/* ✅ Timezone dropdown */}
        <div>
          <label className="block text-sm font-medium mb-1 text-slate-700">
            Timezone <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <select
              name="timezone"
              value={findTimezone(formData.timezone)}
              onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 appearance-none bg-white cursor-pointer"
              required
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
          </div>
          <p className="text-xs text-slate-500 mt-1">Select your timezone for better matching with clients</p>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full sm:w-auto bg-blue-600 text-white px-4 sm:px-6 py-2.5 rounded-lg hover:bg-blue-700 transition font-medium text-sm sm:text-base ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading ? "Updating..." : "Save Changes"}
        </button>

        <div className="pt-4 border-t border-slate-200 mt-4">
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700 text-white font-semibold px-4 py-2 rounded-lg transition"
          >
            Delete My Account
          </button>
        </div>
      </form>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              Are you sure you want to delete your SkillSyncs account? This action is permanent.
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              All your data will be removed and you will be logged out.
            </p>
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-lg border border-slate-200 text-slate-800 hover:bg-slate-50 transition"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700 transition disabled:opacity-70"
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default EditHelperProfile;
